import type { BadgeProps } from '@/components/ui/badge'
import type { ProtectionDeposit } from '@/api/client'

/**
 * Words and colors for a deposit state admin-api has already derived. Nothing
 * here decides a state; it only names one (the UI never infers business state).
 */
export const STATE_PRESENTATION: Record<
  string,
  { label: string; variant: BadgeProps['variant']; hint: string }
> = {
  scheduled: {
    label: 'Scheduled',
    variant: 'secondary',
    hint: 'A hold goes on 24 hours before arrival.',
  },
  hold_due: {
    label: 'Hold due',
    variant: 'warning',
    hint: 'The stay has started and nothing is held yet. The hourly sweep should place it; if this stays, look into it.',
  },
  held_by_uplisting: {
    label: 'Held (Uplisting)',
    variant: 'success',
    hint: 'Uplisting still holds this card, for a stay booked while its deposits were on. We leave it alone and place our own hold if Uplisting stops mid-stay. A capture is made on Uplisting’s payment in Stripe.',
  },
  held: { label: 'Held', variant: 'success', hint: 'A hold is on the guest’s card.' },
  released: {
    label: 'Released',
    variant: 'default',
    hint: 'The hold was given back, or ran out on its own.',
  },
  captured: {
    label: 'Captured',
    variant: 'warning',
    hint: 'Money was taken from the hold, for a damage claim.',
  },
  no_card: {
    label: 'No card',
    variant: 'warning',
    hint: 'No Stripe customer or saved card was found for this booking.',
  },
  failed: {
    label: 'Failed',
    variant: 'destructive',
    hint: 'The last attempt failed. It is retried every hour while the stay lasts.',
  },
  blocked: {
    label: 'Blocked',
    variant: 'destructive',
    hint: 'The card needs the guest (for example a 3-D Secure check), so no hold can be placed.',
  },
  needs_attention: {
    label: 'Needs attention',
    variant: 'destructive',
    hint: 'The state is unknown until it is checked against Stripe, or more than one card matched.',
  },
  not_held: {
    label: 'Not held',
    variant: 'secondary',
    hint: 'The stay ended without a hold, for example before deposits were running.',
  },
  not_required: {
    label: 'Not required',
    variant: 'secondary',
    hint: 'The booking was cancelled, or deposits are off for this listing.',
  },
}

/**
 * The explanation under a booking's status. A simulated hold says so: while
 * automation is off, "a hold is on the guest's card" would be untrue.
 */
export function stateHint(deposit: Pick<ProtectionDeposit, 'state' | 'mode'>): string {
  if (deposit.mode === 'dry_run' && (deposit.state === 'held' || deposit.state === 'released')) {
    return 'Simulated: this is what automation would have done. Nothing was held on the card.'
  }
  return statePresentation(deposit.state).hint
}

export function statePresentation(state: string) {
  return STATE_PRESENTATION[state] ?? { label: state, variant: 'secondary' as const, hint: '' }
}

/** Whole-dollar amounts read better in a list; cents only when there are some. */
export function formatDepositAmount(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '—'
  const dollars = cents / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
  }).format(dollars)
}

/**
 * Stripe's dispute status (`needs_response`, `under_review`, `won`, `lost`,
 * `warning_closed`...) in words. Carried through from Stripe rather than
 * re-modelled, so an unknown future status still reads as itself.
 */
export function disputeLabel(status: string): string {
  const words = status.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Stripe is waiting on the host, so the dispute's evidence deadline still means something. */
export function disputeAwaitsEvidence(status: string | null | undefined): boolean {
  return status === 'needs_response' || status === 'warning_needs_response'
}

/**
 * The Deposit column, in plain words, the way Uplisting's page reads
 * ("authorised, $750.00").
 */
export function describeDeposit(deposit: ProtectionDeposit): string {
  const amount = formatDepositAmount(deposit.amount_cents)
  switch (deposit.state) {
    case 'held':
      return deposit.authorization_expires_at
        ? `held, ${amount} until ${shortDate(deposit.authorization_expires_at)}`
        : `held, ${amount}`
    case 'held_by_uplisting':
      return deposit.authorization_expires_at
        ? `held by Uplisting, ${amount} until ${shortDate(deposit.authorization_expires_at)}`
        : `held by Uplisting, ${amount}`
    case 'captured': {
      const captured = formatDepositAmount(deposit.captured_amount_cents ?? deposit.amount_cents)
      return `captured ${captured} of ${amount}`
    }
    case 'released':
      return `released, ${amount}`
    case 'scheduled':
      return `will hold ${amount}`
    case 'hold_due':
      return `not held yet, ${amount}`
    case 'no_card':
      return `no saved card, ${amount}`
    case 'failed':
      return `hold failed, ${amount}`
    case 'blocked':
      return `card needs guest action, ${amount}`
    case 'needs_attention':
      return `needs a look, ${amount}`
    case 'not_held':
      return 'no hold was placed'
    case 'not_required':
      return deposit.cancelled ? 'booking cancelled' : 'deposits off for this listing'
    default:
      return amount
  }
}

/** "Sep 26": a hold's expiry is always days away, so the year is noise. */
function shortDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** "18 to 25 Sep", or "22 Sep to 25 Oct" across months. */
export function formatStay(checkIn: string | null, checkOut: string | null): string {
  if (!checkIn || !checkOut) return '—'
  const start = new Date(`${checkIn}T00:00:00Z`)
  const end = new Date(`${checkOut}T00:00:00Z`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '—'
  const day = (d: Date) => d.getUTCDate()
  const month = (d: Date) => d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
  const year = (d: Date) => d.getUTCFullYear()
  if (year(start) !== year(end)) {
    return `${day(start)} ${month(start)} ${year(start)} to ${day(end)} ${month(end)} ${year(end)}`
  }
  return month(start) === month(end)
    ? `${day(start)} to ${day(end)} ${month(end)}`
    : `${day(start)} ${month(start)} to ${day(end)} ${month(end)}`
}

const HISTORY_LABELS: Record<string, string> = {
  resolve: 'Card lookup',
  authorize: 'Hold placed',
  would_authorize: 'Hold placed (simulated)',
  reauthorize: 'Hold renewed',
  would_reauthorize: 'Hold renewed (simulated)',
  release: 'Hold released',
  would_release: 'Hold released (simulated)',
  captured_externally: 'Captured in Stripe',
  adopted_uplisting_hold: 'Took over Uplisting’s hold',
  adopted_existing_hold: 'Recovered an interrupted hold',
  uplisting_hold_found: 'Uplisting’s hold found, left to Uplisting',
  uplisting_hold_captured: 'Uplisting’s hold captured in Stripe',
  uplisting_hold_lapsed: 'Uplisting’s hold ran out',
  expiry_refreshed: 'Hold still live, expiry rechecked',
  authorize_failed: 'Hold failed',
  reauthorize_failed: 'Renewal failed',
  release_failed: 'Release failed',
}

/**
 * The detail line under a history entry, only where it is a sentence meant for
 * a person. Most details are bookkeeping ("100000 cents on pm_1U... (booking_payment_pm)")
 * that the amount and status already say better; a failure's reason is the
 * exception, since that is what someone needs to act on.
 */
export function historyDetail(entry: { action: string; detail?: string | null }): string | null {
  if (!entry.detail) return null
  const human =
    entry.action.endsWith('_failed') ||
    entry.action === 'captured_externally' ||
    entry.action === 'expiry_refreshed'
  return human ? entry.detail : null
}

/** A history verb for a human; an unmapped one is shown as-is, never hidden. */
export function historyLabel(action: string): string {
  if (HISTORY_LABELS[action]) return HISTORY_LABELS[action]
  const words = action.replace(/[_.]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}
